import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators';

@customElement('form-checkbox')
export class FormCheckbox extends LitElement {
    @property({ type: String })
    name?: string;

    @property({ type: Boolean })
    checked?: boolean;

    @property({ type: String })
    class_name?: string = '';

    @property({ type: Boolean })
    indeterminate?: boolean;

    @property({ type: Boolean })
    required?: boolean;

    @property({ type: Boolean })
    disabled?: boolean;

    private handleChangeChecked(e: Event) {
        this.checked = (e.target as HTMLInputElement).checked;
        this.dispatchEvent(
            new CustomEvent('change', {
                detail: e.target,
            })
        );
    }

    protected render() {
        return html`
            <div class="form-check" style="min-width: 25%">
                <input
                    class="form-check-input ${this.class_name}"
                    id="${this.name}"
                    type="checkbox"
                    .checked="${this.checked}"
                    .indeterminate="${this.indeterminate}"
                    @change="${this.handleChangeChecked}"
                    ?disabled="${this.disabled}"
                    ?required="${this.required}"
                    aria-required="${this.required === true ? 'true' : 'false'}"
                />
                <label class="form-check-label text-nowrap" for="${this.name}" ?disabled="${this.disabled}">
                    <slot></slot>
                </label>
            </div>
        `;
    }

    static get styles() {
        return css`
            :host {
                --form-checkbox-border-color: var(--theme-checkbox-color);
                --form-checkbox-label-color: var(--theme-input-color);
            }
            .form-check {
                display: flex;
                min-height: 1.5rem;
                align-items: center;
            }

            .form-check-input {
                min-width: 1rem;
                height: 1rem;
                margin: 0.2rem 0;
                vertical-align: top;
                background-color: transparent;
                background-repeat: no-repeat;
                background-position: center;
                background-size: contain;
                border: 2px solid var(--form-checkbox-border-color);
                -webkit-appearance: none;
                -moz-appearance: none;
                appearance: none;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
            }

            .form-check-input[type="checkbox"] {
                border-radius: 0.1875rem;
            }

            .form-check-input:active {
                filter: 0;
            }

            .form-check-input:focus-visible {
                outline: 2px solid var(--main-primary-color, #005fcc);
                outline-offset: 2px;
            }

            .form-check-input:checked {
                background-color: transparent;
            }

            .form-check-input:checked[type="checkbox"] {
                background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23102c3d' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10l3 3l6-6'/%3e%3c/svg%3e");
            }

            .form-check-input.blue:checked[type="checkbox"] {
                background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23689D9D' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10l3 3l6-6'/%3e%3c/svg%3e");
            }

            .form-check-input[type="checkbox"]:indeterminate {
                background-color: var(--main-primary-color);
                border-color: var(--main-primary-color);
                background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10h8'/%3e%3c/svg%3e");
            }

            .form-check-input:disabled {
                pointer-events: none;
                filter: none;
                opacity: 0.5;
            }

            .form-check-input[disabled] ~ .form-check-label,
            .form-check-input:disabled ~ .form-check-label {
                opacity: 0.5;
            }

            .form-check-input[disabled] ~ .form-check-label,
            .form-check-input:disabled ~ .form-check-label {
                opacity: 0.5;
            }

            .form-check-label {
                color: var(--form-checkbox-label-color);
                padding-left: 0.5rem;
                word-break: break-word;
            }
        `;
    }
}
